// src/pages/About/OurTeam.jsx
import React, { useState, useEffect } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
import QuerySection from '../../components/common/QuerySection/QuerySection'; // Import QuerySection component
import { Users, Loader, Mail, Phone, MapPin } from 'lucide-react';

const OurTeam = () => {
    // API base URL
    // ✅ CORRECT - Use backticks for template literals
    const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
    // State for team members
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch team members on component mount
    useEffect(() => {
        fetchTeamMembers();
    }, []);

    // Fetch all team members from API
    const fetchTeamMembers = async () => {
        try {
            setInitialLoading(true);
            setError(null);
            const response = await fetch(`${API_BASE_URL}/team-members/active`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch team members. Server responded with status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Handle different response formats
            let teamMembersData = [];
            if (data.success && Array.isArray(data.data)) {
                teamMembersData = data.data;
            } else if (Array.isArray(data)) {
                teamMembersData = data;
            } else {
                console.warn('Unexpected response format:', data);
                teamMembersData = [];
            }
            
            setTeamMembers(teamMembersData);
            setError(null);
            console.log('Team members fetched successfully:', teamMembersData);
        } catch (error) {
            console.error('Error fetching team members:', error);
            // Don't show any cached/fallback data - only database data or error
            setTeamMembers([]);
            setError(error.message || 'Failed to load team members. Please check your connection and try again.');
        } finally {
            setInitialLoading(false);
            setLoading(false);
        }
    };

    // Get image URL for display - Only Supabase URLs supported
    const getImageUrl = (member) => {
        if (!member.imageUrl) return null;
        
        // Only return if it's a full URL (Supabase URL)
        if (member.imageUrl.startsWith('http')) {
            return member.imageUrl;
        }
        
        // No fallback to local storage - return null for non-Supabase URLs
        console.warn('Non-Supabase URL detected in OurTeam, ignoring:', member.imageUrl);
        return null;
    };

    // Check if position indicates intern
    const isIntern = (position) => {
        if (!position) return false;
        return position.toLowerCase().includes('intern');
    };

    // Separate hero member, regular members, and interns
    const heroMember = teamMembers.find(member => member.isHeroImage || member.displayOrder === 1) || teamMembers[0];
    const filteredMembers = teamMembers.filter(member => member.id !== heroMember?.id);
    const regularMembers = filteredMembers.filter(member => !isIntern(member.position));
    const interns = filteredMembers.filter(member => isIntern(member.position));

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
        },
        
        // Header styling
        heroSection: {
            background: '#1a2332',
            backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px, 30px 30px',
            padding: '2rem 0',
            marginBottom: '0',
            position: 'relative',
            overflow: 'hidden',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
        },
        heroContainer: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem',
            position: 'relative',
            zIndex: 2
        },
        heroTitle: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#ffffff',
            margin: '0',
            textAlign: 'center',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
        },
        
        // Large Hero Image Section
        heroImageSection: {
            position: 'relative',
            height: '80vh',
            minHeight: '700px',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'scroll',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            width: '100%'
        },
        heroOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1
        },
        heroContent: {
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
            color: 'white',
            maxWidth: '800px',
            padding: '0 2rem'
        },
        heroImageTitle: {
            fontSize: '3.5rem',
            fontWeight: '700',
            marginBottom: '1rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            letterSpacing: '-0.025em'
        },
        heroImageSubtitle: {
            fontSize: '1.5rem',
            fontWeight: '400',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            opacity: 0.95,
            lineHeight: '1.4'
        },
        
        // Loading spinner
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            color: '#6b7280'
        },
        spinner: {
            marginRight: '0.5rem'
        },
        
        // Main Section
        mainSection: {
            backgroundColor: '#f8fafc',
            padding: '3rem 0 5rem 0',
            flex: 1
        },
        container: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem'
        },
        
        // Section Header
        sectionHeader: {
            textAlign: 'center',
            marginBottom: '4rem'
        },
        sectionTitle: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '1rem',
            letterSpacing: '-0.025em'
        },
        sectionSubtitle: {
            fontSize: '1.125rem',
            color: '#4a5568',
            marginBottom: '2rem',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
        },
        
        // Intern Section Header (smaller and different styling)
        internSectionHeader: {
            textAlign: 'center',
            marginBottom: '3rem',
            marginTop: '5rem'
        },
        internSectionTitle: {
            fontSize: '2rem',
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '0.5rem',
            letterSpacing: '-0.025em'
        },
        internSectionSubtitle: {
            fontSize: '1rem',
            color: '#718096',
            marginBottom: '1rem',
            maxWidth: '500px',
            margin: '0 auto',
            lineHeight: '1.5'
        },
        
        // Team Grid - 3 cards per row for regular members
        teamGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '2rem',
            marginTop: '2rem'
        },
        
        // Intern Grid - 2 cards per row for interns (smaller)
        internGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.5rem',
            marginTop: '2rem',
            maxWidth: '800px',
            margin: '0 auto'
        },
        
        // Regular Card Design
        teamCard: {
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e2e8f0',
            position: 'relative'
        },
        
        // Intern Card Design (slightly smaller and different border)
        internCard: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
            border: '2px solid #e6fffa',
            position: 'relative'
        },
        
        // Image section for regular cards
        imageSection: {
            position: 'relative',
            height: '280px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        
        // Smaller image section for intern cards
        internImageSection: {
            position: 'relative',
            height: '220px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #4fd1c7 0%, #81e6d9 100%)'
        },
        
        memberImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover'
        },
        imagePlaceholder: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontSize: '4rem'
        },
        
        internImagePlaceholder: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #4fd1c7 0%, #81e6d9 100%)',
            color: 'white',
            fontSize: '3rem'
        },
        
        // Card content
        cardContent: {
            padding: '1.5rem'
        },
        
        // Intern card content (smaller padding)
        internCardContent: {
            padding: '1.25rem'
        },
        
        memberName: {
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '0.5rem',
            textAlign: 'center'
        },
        
        internName: {
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1a202c',
            marginBottom: '0.5rem',
            textAlign: 'center'
        },
        
        memberPosition: {
            fontSize: '1rem',
            color: '#3182ce',
            fontWeight: '600',
            marginBottom: '0.5rem',
            textAlign: 'center'
        },
        
        internPosition: {
            fontSize: '0.875rem',
            color: '#319795',
            fontWeight: '600',
            marginBottom: '0.5rem',
            textAlign: 'center'
        },
        
        memberQualifications: {
            fontSize: '0.875rem',
            color: '#718096',
            marginBottom: '1rem',
            textAlign: 'center',
            fontStyle: 'italic'
        },
        
        internQualifications: {
            fontSize: '0.8rem',
            color: '#718096',
            marginBottom: '0.75rem',
            textAlign: 'center',
            fontStyle: 'italic'
        },
        
        memberDescription: {
            fontSize: '0.875rem',
            color: '#4a5568',
            lineHeight: '1.5',
            marginBottom: '1rem',
            textAlign: 'center'
        },
        
        internDescription: {
            fontSize: '0.8rem',
            color: '#4a5568',
            lineHeight: '1.5',
            marginBottom: '0.75rem',
            textAlign: 'center'
        },
        
        // Contact info
        contactInfo: {
            borderTop: '1px solid #e2e8f0',
            paddingTop: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
        },
        
        internContactInfo: {
            borderTop: '1px solid #e6fffa',
            paddingTop: '0.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.4rem'
        },
        
        contactItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.75rem',
            color: '#718096'
        },
        
        internContactItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.7rem',
            color: '#718096'
        },
        
        contactIcon: {
            flexShrink: 0
        },
        
        // Empty state
        emptyState: {
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#6b7280',
            gridColumn: '1 / -1'
        },
        emptyStateTitle: {
            fontSize: '1.5rem',
            marginBottom: '0.5rem',
            color: '#1a202c'
        },
        
        // Divider between sections
        sectionDivider: {
            height: '1px',
            backgroundColor: '#e2e8f0',
            margin: '4rem 0',
            maxWidth: '200px',
            marginLeft: 'auto',
            marginRight: 'auto'
        }
    };

    // Show loading screen on initial load
    if (initialLoading) {
        return (
            <div style={styles.pageContainer}>
                <Header />
                <div style={styles.loadingContainer}>
                    <Loader size={32} style={styles.spinner} className="animate-spin" />
                    <span style={{ fontSize: '1.125rem' }}>Loading team members...</span>
                </div>
                <Layout />
                <Footer />
            </div>
        );
    }

    return (
        <>
            <style>{`
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .team-card {
                    /* No hover effects - static cards */
                }
                
                .member-image {
                    /* No hover effects - static images */
                }
                
                .hero-image-section {
                    /* No hover effect for hero section */
                }
                
                /* Responsive - 2 cards on tablets */
                @media (max-width: 1024px) {
                    .team-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                        gap: 1.5rem !important;
                    }
                    
                    .intern-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                        gap: 1.25rem !important;
                    }
                    
                    .hero-title {
                        font-size: 2rem !important;
                    }
                    
                    .hero-image-title {
                        font-size: 2.5rem !important;
                    }
                    
                    .hero-image-subtitle {
                        font-size: 1.25rem !important;
                    }
                    
                    .section-title {
                        font-size: 2rem !important;
                    }
                    
                    .intern-section-title {
                        font-size: 1.75rem !important;
                    }
                    
                    .image-section {
                        height: 240px !important;
                    }
                    
                    .intern-image-section {
                        height: 200px !important;
                    }
                    
                    .hero-image-section {
                        height: 70vh !important;
                        min-height: 600px !important;
                    }
                }
                
                /* Single column on mobile */
                @media (max-width: 768px) {
                    .team-grid {
                        grid-template-columns: 1fr !important;
                        gap: 1.5rem !important;
                    }
                    
                    .intern-grid {
                        grid-template-columns: 1fr !important;
                        gap: 1.25rem !important;
                    }
                    
                    .hero-title {
                        font-size: 1.8rem !important;
                    }
                    
                    .hero-image-title {
                        font-size: 2rem !important;
                    }
                    
                    .hero-image-subtitle {
                        font-size: 1.125rem !important;
                    }
                    
                    .section-title {
                        font-size: 1.8rem !important;
                    }
                    
                    .intern-section-title {
                        font-size: 1.5rem !important;
                    }
                    
                    .image-section {
                        height: 280px !important;
                    }
                    
                    .intern-image-section {
                        height: 240px !important;
                    }
                    
                    .card-content {
                        padding: 1.25rem !important;
                    }
                    
                    .intern-card-content {
                        padding: 1rem !important;
                    }
                    
                    .container {
                        padding: 0 1rem !important;
                    }
                    
                    .hero-content {
                        padding: 0 1rem !important;
                    }
                    
                    .hero-image-section {
                        height: 60vh !important;
                        min-height: 500px !important;
                    }
                }
                
                @media (max-width: 640px) {
                    .main-section {
                        padding: 2rem 0 3rem 0 !important;
                    }
                    
                    .section-header {
                        margin-bottom: 2rem !important;
                    }
                    
                    .intern-section-header {
                        margin-bottom: 2rem !important;
                        margin-top: 3rem !important;
                    }
                    
                    .hero-title {
                        font-size: 1.5rem !important;
                    }
                    
                    .hero-image-title {
                        font-size: 1.75rem !important;
                    }
                    
                    .hero-image-subtitle {
                        font-size: 1rem !important;
                    }
                    
                    .section-title {
                        font-size: 1.5rem !important;
                    }
                    
                    .intern-section-title {
                        font-size: 1.25rem !important;
                    }
                    
                    .section-subtitle {
                        font-size: 1rem !important;
                    }
                    
                    .intern-section-subtitle {
                        font-size: 0.9rem !important;
                    }
                    
                    .hero-image-section {
                        height: 55vh !important;
                        min-height: 450px !important;
                    }
                }
            `}</style>

            <div style={styles.pageContainer}>
                {/* Header */}
                <Header />

                {/* Large Hero Image Section */}
                {heroMember && (
                    <section 
                        style={{
                            ...styles.heroImageSection,
                            backgroundImage: `url(${getImageUrl(heroMember) || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&h=1080&fit=crop'})`
                        }} 
                        className="hero-image-section"
                    >
                        <div style={styles.heroOverlay}></div>
                        <div style={styles.heroContent}>
                            <h1 style={styles.heroImageTitle} className="hero-image-title">
                                Our Team
                            </h1>
                            <p style={styles.heroImageSubtitle} className="hero-image-subtitle">
                                Prosperity Is Your Birth Right, Please Attain It
                            </p>
                        </div>
                    </section>
                )}

                {/* Main Content */}
                <main style={styles.mainSection} className="main-section">
                    <div style={styles.container} className="container">

                        {/* Section Header for Regular Team Members */}
                        {regularMembers.length > 0 && (
                            <div style={styles.sectionHeader} className="section-header">
                                <h2 style={styles.sectionTitle} className="section-title">
                                    Our Team
                                </h2>
                                <p style={styles.sectionSubtitle} className="section-subtitle">
                                    Our dedicated professionals committed to your financial success and growth
                                </p>
                            </div>
                        )}

                        {/* Regular Team Members Grid */}
                        {regularMembers.length > 0 && (
                            <div style={styles.teamGrid} className="team-grid">
                                {regularMembers.map((member) => {
                                    const imageUrl = getImageUrl(member);
                                    return (
                                        <div key={member.id} style={styles.teamCard} className="team-card">
                                            {/* Image Section */}
                                            <div style={styles.imageSection} className="image-section">
                                                {imageUrl ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={member.name}
                                                        style={styles.memberImage}
                                                        className="member-image"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentNode.querySelector('.image-placeholder').style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                
                                                <div 
                                                    className="image-placeholder"
                                                    style={{
                                                        ...styles.imagePlaceholder,
                                                        display: imageUrl ? 'none' : 'flex'
                                                    }}
                                                >
                                                    👤
                                                </div>
                                            </div>

                                            {/* Card Content */}
                                            <div style={styles.cardContent} className="card-content">
                                                <h3 style={styles.memberName}>{member.name}</h3>
                                                <p style={styles.memberPosition}>{member.position}</p>
                                                
                                                {member.qualifications && (
                                                    <p style={styles.memberQualifications}>
                                                        {member.qualifications}
                                                    </p>
                                                )}
                                                
                                                {member.description && (
                                                    <p style={styles.memberDescription}>
                                                        {member.description}
                                                    </p>
                                                )}
                                                
                                                {/* Contact Information */}
                                                {(member.email || member.phone || member.location) && (
                                                    <div style={styles.contactInfo}>
                                                        {member.email && (
                                                            <div style={styles.contactItem}>
                                                                <Mail size={12} style={styles.contactIcon} />
                                                                <span>{member.email}</span>
                                                            </div>
                                                        )}
                                                        {member.phone && (
                                                            <div style={styles.contactItem}>
                                                                <Phone size={12} style={styles.contactIcon} />
                                                                <span>{member.phone}</span>
                                                            </div>
                                                        )}
                                                        {member.location && (
                                                            <div style={styles.contactItem}>
                                                                <MapPin size={12} style={styles.contactIcon} />
                                                                <span>{member.location}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Section Divider */}
                        {regularMembers.length > 0 && interns.length > 0 && (
                            <div style={styles.sectionDivider}></div>
                        )}

                        {/* Intern Section Header */}
                        {interns.length > 0 && (
                            <div style={styles.internSectionHeader} className="intern-section-header">
                                <h2 style={styles.internSectionTitle} className="intern-section-title">
                                    Our Interns
                                </h2>
                                <p style={styles.internSectionSubtitle} className="intern-section-subtitle">
                                    Bright minds gaining valuable experience and contributing fresh perspectives
                                </p>
                            </div>
                        )}

                        {/* Interns Grid */}
                        {interns.length > 0 && (
                            <div style={styles.internGrid} className="intern-grid">
                                {interns.map((intern) => {
                                    const imageUrl = getImageUrl(intern);
                                    return (
                                        <div key={intern.id} style={styles.internCard} className="intern-card">
                                            {/* Image Section */}
                                            <div style={styles.internImageSection} className="intern-image-section">
                                                {imageUrl ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={intern.name}
                                                        style={styles.memberImage}
                                                        className="member-image"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentNode.querySelector('.intern-image-placeholder').style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                
                                                <div 
                                                    className="intern-image-placeholder"
                                                    style={{
                                                        ...styles.internImagePlaceholder,
                                                        display: imageUrl ? 'none' : 'flex'
                                                    }}
                                                >
                                                    🎓
                                                </div>
                                            </div>

                                            {/* Card Content */}
                                            <div style={styles.internCardContent} className="intern-card-content">
                                                <h3 style={styles.internName}>{intern.name}</h3>
                                                <p style={styles.internPosition}>{intern.position}</p>
                                                
                                                {intern.qualifications && (
                                                    <p style={styles.internQualifications}>
                                                        {intern.qualifications}
                                                    </p>
                                                )}
                                                
                                                {intern.description && (
                                                    <p style={styles.internDescription}>
                                                        {intern.description}
                                                    </p>
                                                )}
                                                
                                                {/* Contact Information */}
                                                {(intern.email || intern.phone || intern.location) && (
                                                    <div style={styles.internContactInfo}>
                                                        {intern.email && (
                                                            <div style={styles.internContactItem}>
                                                                <Mail size={10} style={styles.contactIcon} />
                                                                <span>{intern.email}</span>
                                                            </div>
                                                        )}
                                                        {intern.phone && (
                                                            <div style={styles.internContactItem}>
                                                                <Phone size={10} style={styles.contactIcon} />
                                                                <span>{intern.phone}</span>
                                                            </div>
                                                        )}
                                                        {intern.location && (
                                                            <div style={styles.internContactItem}>
                                                                <MapPin size={10} style={styles.contactIcon} />
                                                                <span>{intern.location}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Empty State or Error */}
                        {teamMembers.length === 0 && !loading && (
                            <div style={styles.emptyState}>
                                <Users size={64} style={{ marginBottom: '1rem', opacity: 0.3, color: error ? '#f56565' : '#cbd5e0' }} />
                                <h3 style={styles.emptyStateTitle}>
                                    {error ? 'Unable to Load Team Members' : 'No Team Members Available'}
                                </h3>
                                <p style={{ fontSize: '1rem', color: error ? '#e53e3e' : '#718096' }}>
                                    {error || 'Team information is not available at this time.'}
                                </p>
                                {error && (
                                    <button 
                                        onClick={() => {
                                            setLoading(true);
                                            fetchTeamMembers();
                                        }}
                                        style={{
                                            marginTop: '1rem',
                                            padding: '0.5rem 1.5rem',
                                            backgroundColor: '#4a5568',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0.375rem',
                                            cursor: 'pointer',
                                            fontSize: '1rem'
                                        }}
                                    >
                                        Retry
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Loading state */}
                        {loading && teamMembers.length === 0 && (
                            <div style={styles.loadingContainer}>
                                <Loader size={32} style={styles.spinner} className="animate-spin" />
                                <span style={{ fontSize: '1.125rem' }}>Loading team members...</span>
                            </div>
                        )}
                    </div>
                </main>

                {/* Layout */}
                <Layout />

                <QuerySection/>

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default OurTeam;